/** Tag model / chuyên gia / tool / nguồn dữ liệu cho mỗi câu trả lời hub chat. */
import React, { useMemo } from 'react';
import { Tag, Tooltip } from 'antd';
import {
  ApiOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  GlobalOutlined,
  RobotOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import type { AiHubChatRunMeta } from '@/types/ai-agent';
import { chatDisplayText } from './chatDisplayText';
import { agentLabel } from './useAiChat';
import styles from './AiChatView.module.css';

const PROVIDER_LABEL: Record<string, string> = {
  ollama: 'Ollama',
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google: 'Google',
};

const SOURCE_LABEL: Record<string, string> = {
  internal: 'Dữ liệu nội bộ',
  web: 'Tìm web',
  inprocess: 'Chuyên gia (local)',
  none: 'Không có nguồn',
};

function modelTagText(meta: AiHubChatRunMeta): string {
  const provider = PROVIDER_LABEL[meta.provider ?? ''] ?? meta.provider;
  const model = (meta.model_label || meta.model || '').trim();
  if (provider && model) return `${provider} · ${model}`;
  return model || provider || 'Model';
}

export interface ChatRunMetaTagsProps {
  meta?: AiHubChatRunMeta;
}

export const ChatRunMetaTags: React.FC<ChatRunMetaTagsProps> = ({ meta }) => {
  const tags = useMemo(() => {
    if (!meta) return null;
    const out: React.ReactNode[] = [];

    const modelText = modelTagText(meta);
    if (modelText) {
      out.push(
        <Tooltip key="model" title="Model trả lời cuối (stream)">
          <Tag icon={<RobotOutlined />} color="processing" bordered={false} className={styles.metaTag}>
            {modelText}
          </Tag>
        </Tooltip>,
      );
    }

    if (meta.thinking) {
      out.push(
        <Tag key="thinking" icon={<ThunderboltOutlined />} bordered={false} className={styles.metaTag}>
          Thinking
        </Tag>,
      );
    } else if (meta.thinking === false && meta.model) {
      out.push(
        <Tag key="std" bordered={false} className={styles.metaTagMuted}>
          Standard
        </Tag>,
      );
    }

    if (typeof meta.threshold === 'number') {
      out.push(
        <Tooltip key="temp" title="Threshold (temperature)">
          <Tag bordered={false} className={styles.metaTagMuted}>
            T={meta.threshold.toFixed(1)}
          </Tag>
        </Tooltip>,
      );
    }

    const agentDetails = meta.agents ?? [];
    if (!agentDetails.length) {
      (meta.specialists ?? []).forEach((id) => {
        out.push(
          <Tag key={`sp-${id}`} color="blue" bordered={false} className={styles.metaTag}>
            {agentLabel(id)}
          </Tag>,
        );
      });
    }

    agentDetails.forEach((a) => {
      const parts = [agentLabel(a.agent)];
      if (a.industry) parts.push(chatDisplayText(a.industry, 40));
      if (typeof a.docs === 'number' && a.docs > 0) parts.push(`${a.docs} dòng`);
      const src = a.source ? SOURCE_LABEL[a.source] ?? a.source : '';
      if (src) parts.push(src);
      out.push(
        <Tooltip key={`ar-${a.agent}`} title="Chuyên gia & nguồn retrieve">
          <Tag icon={<DatabaseOutlined />} bordered={false} className={styles.metaTag}>
            {parts.join(' · ')}
          </Tag>
        </Tooltip>,
      );
    });

    (meta.tools ?? []).forEach((tool) => {
      const isWeb = tool === 'web_search';
      const isCsv = tool === 'success_factor.csv';
      out.push(
        <Tooltip key={`tool-${tool}`} title="Tool / kho đã dùng">
          <Tag
            icon={isWeb ? <GlobalOutlined /> : isCsv ? <DatabaseOutlined /> : <ApiOutlined />}
            bordered={false}
            className={styles.metaTag}
          >
            {isWeb ? 'Web search' : isCsv ? 'CSF (CSV nội bộ)' : tool}
          </Tag>
        </Tooltip>,
      );
    });

    const kbFromMeta = meta.kb_files ?? [];
    const kbUnique = [...new Set(kbFromMeta.filter(Boolean))];
    kbUnique.forEach((file) => {
      out.push(
        <Tooltip key={`kb-${file}`} title="Tài liệu doanh nghiệp (KB)">
          <Tag icon={<FileTextOutlined />} bordered={false} className={styles.metaTag}>
            {file}
          </Tag>
        </Tooltip>,
      );
    });

    return out.length ? out : null;
  }, [meta]);

  if (!tags) return null;
  return <div className={styles.runMetaTags}>{tags}</div>;
};

export default ChatRunMetaTags;
